import React from 'react';
import { AutocompleteRenderInputParams } from '@mui/material';
import { GridFilterItem, GridFilterOperator } from '@mui/x-data-grid';

import { CenteredCircularProgress } from 'components/Progress';

import { useInfiniteSearch } from 'utils/hooks/infiniteSearch';

import { APIRequest, SearchRequest, UuidableName } from 'utils/api/types';

import { StyledTextField } from 'components/Input/styles';

import { createFilterRegex } from 'utils/regex';
import { notifyCacheUpdate } from '../cacheEvents';

import { StyledAutocomplete, StyledOption } from './styles';
import { SearchProps } from '../types';

const loadingOption = { uuid: 'loading', name: 'Loading...' };

const SearchInput: React.FunctionComponent<SearchProps> = ({ apiSearch, loadDetails, valueKey, applyValue, item }) => {
  const { results, loading, loadingMore, hasMore, loadMore, searchQuery } = useInfiniteSearch(apiSearch);
  const [selected, setSelected] = React.useState<UuidableName | null | undefined>(undefined);

  React.useEffect(() => {
    if (!item.value) {
      setSelected(null);
      return undefined;
    }
    const uuid = String(item.value);
    let cancelled = false;
    loadDetails(uuid)()
      .then(({ data }) => {
        if (!cancelled) {
          setSelected((previous) => previous || { uuid, name: data[valueKey] });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSelected((previous) => previous || null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <StyledAutocomplete
      loadingMore={loadingMore}
      hasMore={hasMore}
      onLoadMore={loadMore}
      options={selected === undefined ? [loadingOption] : results}
      value={selected === undefined ? loadingOption : selected}
      isOptionEqualToValue={(option: UuidableName | null, val: UuidableName | null) =>
        val?.uuid !== undefined && val?.uuid === option?.uuid
      }
      renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: UuidableName) =>
        option.uuid === loadingOption.uuid ? (
          <StyledOption>
            <CenteredCircularProgress />
          </StyledOption>
        ) : (
          <StyledOption {...props}>{option.name}</StyledOption>
        )
      }
      getOptionLabel={(option: UuidableName | null) => (option ? option.name : '')}
      getOptionDisabled={(option: UuidableName) => option.uuid === loadingOption.uuid || option.uuid === selected?.uuid}
      renderInput={(params: AutocompleteRenderInputParams) => (
        <StyledTextField
          {...params}
          placeholder="Field Value"
          value={selected?.name || ''}
          onChange={(e) => {
            searchQuery(e.target.value);
          }}
        />
      )}
      loading={loading}
      noOptionsText="-"
      onChange={(_: React.SyntheticEvent, rawValue: UuidableName | null) => {
        setSelected(rawValue);
        applyValue({ ...item, value: rawValue?.uuid });
      }}
      fullWidth
    />
  );
};

const cache = new Map<string, string | undefined>();

export default function <T>(
  apiSearch: (params: SearchRequest) => APIRequest<T[]>,
  loadDetails: unknown,
  valueKey: string
): GridFilterOperator[] {
  return [
    {
      label: 'is',
      value: 'is',
      getApplyFilterFn: (filterItem: GridFilterItem) => {
        if (!filterItem.value) {
          return null;
        }

        const filterRegex = createFilterRegex(filterItem.value);
        return ({ value }: { value: string }): boolean => (value != null ? filterRegex.test(value.toString()) : false);
      },
      InputComponent: SearchInput,
      InputComponentProps: { apiSearch, loadDetails, valueKey },
      getValueAsString: (value: string) => {
        if (!cache.has(value)) {
          cache.set(value, undefined);
          // @ts-ignore
          loadDetails(value)()
            .then(({ data }: { data: Record<string, string> }) => {
              cache.set(value, data[valueKey]);
              notifyCacheUpdate();
            })
            .catch(() => {
              cache.delete(value);
            });
        }

        return cache.get(value) || 'loading...';
      },
    },
  ] as unknown as GridFilterOperator[];
}
